import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import api from '../api';
import {
  asArray,
  formatSlotDateTime,
  formatSlotTime,
  groupSlotsByDay,
  propertyLabel,
} from '../utils/showings';

/**
 * Prospect self-service: view, reschedule, or cancel a showing (token from email).
 */
const ShowingBookingManage = () => {
  const { token } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [showing, setShowing] = useState(null);
  const [timeZone, setTimeZone] = useState('');
  const [mode, setMode] = useState('view'); // view | reschedule
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);

  const loadShowing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/showings/public/manage/${encodeURIComponent(token)}`);
      setShowing(res.data);
      setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch (err) {
      setError(err.response?.data?.error || 'This booking link is not valid or has expired.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadShowing();
  }, [loadShowing]);

  const fetchSlots = useCallback(async () => {
    if (!showing?.propertyId) return;
    setLoadingSlots(true);
    setError(null);
    try {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        propertyId: showing.propertyId,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });
      if (showing.unitId) params.set('unitId', showing.unitId);
      const res = await api.get(`/api/showings/public/available?${params.toString()}`);
      setSlots(asArray(res.data?.availableSlots ?? res.data));
      setTimeZone(res.data?.timeZone || timeZone);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load open times.');
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [showing, timeZone]);

  useEffect(() => {
    if (mode === 'reschedule' && showing?.canChange) fetchSlots();
  }, [mode, showing, fetchSlots]);

  const days = useMemo(() => groupSlotsByDay(slots, timeZone), [slots, timeZone]);
  const timesForDay = useMemo(
    () => days.find((d) => d.dateKey === selectedDay)?.slots || [],
    [days, selectedDay]
  );

  const handleCancel = async () => {
    if (!window.confirm('Cancel this showing?')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(
        `/api/showings/public/manage/${encodeURIComponent(token)}/cancel`
      );
      setShowing(res.data);
      setMode('view');
      setInfo('Your showing was cancelled.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not cancel this showing.');
    } finally {
      setBusy(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedSlot) {
      setError('Please pick a new time.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(
        `/api/showings/public/manage/${encodeURIComponent(token)}/reschedule`,
        { scheduledAt: selectedSlot.start }
      );
      setShowing(res.data);
      setMode('view');
      setSelectedDay('');
      setSelectedSlot(null);
      setInfo('Your new time is in. The property manager will confirm it by email.');
    } catch (err) {
      setError(err.response?.data?.error || 'That time is no longer open. Please pick another.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!showing) {
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
        <Alert severity="error">{error || 'Booking not found.'}</Alert>
      </Box>
    );
  }

  const cancelled = showing.status === 'cancelled';
  const place = [
    propertyLabel({ name: showing.propertyName, address: showing.propertyAddress }),
    showing.unitName,
  ]
    .filter(Boolean)
    .join(', unit ');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Your showing
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {cancelled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This showing was cancelled.
            </Alert>
          )}
          <Typography variant="subtitle2" color="text.secondary">
            Property
          </Typography>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {place || 'Property showing'}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            When
          </Typography>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {showing.when || formatSlotDateTime(showing.scheduledAt, timeZone)}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Status
          </Typography>
          <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
            {showing.status === 'pending' ? 'Pending confirmation' : showing.status}
          </Typography>
        </CardContent>
      </Card>

      {mode === 'view' && showing.canChange && (
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
          <Button variant="contained" onClick={() => setMode('reschedule')} disabled={busy}>
            Pick a new time
          </Button>
          <Button variant="outlined" color="error" onClick={handleCancel} disabled={busy}>
            Cancel showing
          </Button>
        </Box>
      )}

      {mode === 'reschedule' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pick a new time
            </Typography>
            {loadingSlots ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : days.length === 0 ? (
              <Alert severity="info">No open times in the next 30 days. Contact the property manager.</Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  1. Choose a day
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {days.map((day) => (
                    <Grid item xs={6} sm={4} key={day.dateKey}>
                      <Button
                        fullWidth
                        size="small"
                        variant={selectedDay === day.dateKey ? 'contained' : 'outlined'}
                        onClick={() => {
                          setSelectedDay(day.dateKey);
                          setSelectedSlot(null);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        {day.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                {selectedDay && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      2. Choose a time
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      {timesForDay.map((slot) => (
                        <Grid item xs={4} sm={3} key={slot.start}>
                          <Button
                            fullWidth
                            size="small"
                            variant={selectedSlot?.start === slot.start ? 'contained' : 'outlined'}
                            onClick={() => setSelectedSlot(slot)}
                            sx={{ textTransform: 'none' }}
                          >
                            {formatSlotTime(slot.start, timeZone)}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={handleReschedule}
                    disabled={busy || !selectedSlot}
                    startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    Save new time
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setMode('view');
                      setSelectedDay('');
                      setSelectedSlot(null);
                    }}
                    disabled={busy}
                  >
                    Back
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {cancelled && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <CheckCircleIcon color="disabled" sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            You can book again from the link the property manager shared with you.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ShowingBookingManage;
